/**
 * Message Composition Hook
 *
 * Manages message draft, file attachments, and sending logic.
 * Extracted from dashboard page for better modularity and debugging.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
type UseMessageCompositionOptions = {
  selectedThreadId: string | null;
  sendMessage: (content: string, files?: File[], overrideThreadId?: string) => Promise<void>;
  createThread?: () => Promise<string | null>;
  onThreadCreated?: () => void;
};
type UseMessageCompositionResult = {
  draft: string;
  setDraft: (value: string) => void;
  attachedFiles: File[];
  setAttachedFiles: (files: File[]) => void;
  handleSend: () => Promise<void>;
};
export function useMessageComposition(options) {
  const $ = _c(12);
  const {
    selectedThreadId,
    sendMessage,
    createThread,
    onThreadCreated
  } = options;
  const [draft, setDraft] = useState("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [attachedFiles, setAttachedFiles] = useState(t0);
  let t1;
  if ($[1] !== attachedFiles || $[2] !== createThread || $[3] !== draft || $[4] !== onThreadCreated || $[5] !== selectedThreadId || $[6] !== sendMessage) {
    t1 = async () => {
      const content = draft.trim();
      if (!content && attachedFiles.length === 0) {
        return;
      }
      let threadId = selectedThreadId;
      if (!threadId && createThread) {
        const newThreadId = await createThread();
        if (!newThreadId) {
          console.error("Failed to create thread");
          return;
        }
        threadId = newThreadId;
        onThreadCreated?.();
      }
      if (!threadId) {
        console.error("No thread ID available to send message");
        return;
      }
      const filesToSend = [...attachedFiles];
      setDraft("");
      setAttachedFiles([]);
      await sendMessage(content || "Here are the files:", filesToSend, threadId);
    };
    $[1] = attachedFiles;
    $[2] = createThread;
    $[3] = draft;
    $[4] = onThreadCreated;
    $[5] = selectedThreadId;
    $[6] = sendMessage;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleSend = t1;
  let t2;
  if ($[8] !== attachedFiles || $[9] !== draft || $[10] !== handleSend) {
    t2 = {
      draft,
      setDraft,
      attachedFiles,
      setAttachedFiles,
      handleSend
    };
    $[8] = attachedFiles;
    $[9] = draft;
    $[10] = handleSend;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}